import { BooleanField, TextField, TextFieldInputProps } from "@hex-insights/forms";
import { RadioTextInput } from "../../../Components/RadioTextInput";
import { RelationshipFormValues } from "../../FormUtilities";
import { GuardianSelectField } from "../Guardian";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";

/**
 * Generic props for fields of the Relationship model.
 */
type FieldProps<K extends keyof RelationshipFormValues.Base = keyof RelationshipFormValues.Base> = BaseFieldProps<
	Pick<RelationshipFormValues.Base, K>
>;

/**
 * Field component for the `relationshipType` field of the Relationship model.
 */
export function RelationshipType({
	formState,
	label = "Relationship to Student",
	...props
}: FieldProps<"relationshipType">) {
	return (
		<TextField formState={formState} name="relationshipType" label={label} Input={RelationshipTypeInput} {...props} />
	);
}

const relationshipTypeOptions = [{ value: "Father" }, { value: "Mother" }];

function RelationshipTypeInput(props: TextFieldInputProps) {
	return <RadioTextInput options={relationshipTypeOptions} {...props} className={undefined} />;
}

/**
 * Field component for the `isPrimary` field of the Relationship model.
 */
export function IsPrimary({ formState, label = "Primary Contact", ...props }: FieldProps<"isPrimary">) {
	return <BooleanField formState={formState} name="isPrimary" label={label} {...props} />;
}

/**
 * Field component for the `isFinancialContact` field of the Relationship model.
 */
export function IsFinancialContact({
	formState,
	label = "Financial Contact",
	...props
}: FieldProps<"isFinancialContact">) {
	return <BooleanField formState={formState} name="isFinancialContact" label={label} {...props} />;
}

/**
 * Field component for the `isEmergencyContact` field of the Relationship model.
 */
export function IsEmergencyContact({
	formState,
	label = "Emergency Contact",
	...props
}: FieldProps<"isEmergencyContact">) {
	return <BooleanField formState={formState} name="isEmergencyContact" label={label} {...props} />;
}

/**
 * Field component for the `isAuthorizedForPickup` field of the Relationship model.
 */
export function IsAuthorizedForPickup({
	formState,
	label = "Authorized for Pickup",
	...props
}: FieldProps<"isAuthorizedForPickup">) {
	return <BooleanField formState={formState} name="isAuthorizedForPickup" label={label} {...props} />;
}

/**
 * Field component for the `livesWithStudent` field of the Relationship model.
 */
export function LivesWithStudent({ formState, ...props }: FieldProps<"livesWithStudent">) {
	return <BooleanField formState={formState} name="livesWithStudent" {...props} />;
}

/**
 * Field component for the `hasCustody` field of the Relationship model.
 */
export function HasCustody({ formState, ...props }: FieldProps<"hasCustody">) {
	return <BooleanField formState={formState} name="hasCustody" {...props} />;
}

export namespace Guardian {
	export type SelectProps = FieldProps<"guardianID"> &
		Omit<GuardianSelectField.SelectProps<RelationshipFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `guardian` edge of the Relationship model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <GuardianSelectField.Select formState={formState} name="guardianID" {...props} />;
	}
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<StudentSelectField.SelectProps<RelationshipFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `student` edge of the Relationship model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" {...props} />;
	}
}
